import React from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '@iconify/react';
import { Spinner } from '@nextui-org/react';
import { CtyButton } from '@circuitry-ai/doc-ui';
import type { PronunciationDictionary } from '@circuitry-ai/doc-data';
import DictionaryPaginationBar from './DictionaryPaginationBar';

interface DictionarySidebarProps {
  dictionaries: PronunciationDictionary[];
  selectedName: string | null;
  isLoading: boolean;
  onSelect: (name: string) => void;
  onCreate: () => void;
  page: number;
  perPage: number;
  totalCount: number;
  onPageChange: (page: number) => void;
  onPerPageChange: (perPage: number) => void;
}

const DictionarySidebar: React.FC<DictionarySidebarProps> = ({
  dictionaries,
  selectedName,
  isLoading,
  onSelect,
  onCreate,
  page,
  perPage,
  totalCount,
  onPageChange,
  onPerPageChange,
}) => {
  const { t } = useTranslation();

  return (
    <aside className="w-full lg:w-[280px] shrink-0 flex flex-col gap-3">
      <CtyButton
        color="primary"
        className="w-full font-medium"
        onPress={onCreate}
        startContent={<Icon icon="mdi:plus" className="w-4 h-4" />}
      >
        {t('New dictionary')}
      </CtyButton>

      <div className="flex flex-col gap-2 min-h-[200px]">
        {isLoading && (
          <div className="flex items-center justify-center gap-2 py-8 text-default-500">
            <Spinner size="sm" />
            <span className="text-sm">{t('Loading...')}</span>
          </div>
        )}

        {!isLoading && dictionaries.length === 0 && (
          <div className="rounded-xl border border-dashed border-divider bg-default-50 px-4 py-6 text-center text-sm text-default-500">
            {t('No dictionaries yet. Create one to get started.')}
          </div>
        )}

        {!isLoading &&
          dictionaries.map((dictionary) => {
            const name = String(dictionary.name ?? '').trim();
            if (!name) return null;
            const isSelected = selectedName === name;
            const entryCount = dictionary.items_count ?? dictionary.items?.length ?? 0;

            return (
              <button
                key={dictionary.id ?? name}
                type="button"
                onClick={() => onSelect(name)}
                className={`w-full text-left rounded-xl border px-3 py-3 transition-colors ${
                  isSelected
                    ? 'border-primary bg-primary/5 shadow-sm'
                    : 'border-divider bg-content1 hover:bg-default-50'
                }`}
              >
                <div className="flex items-start gap-3">
                  <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-default-100 text-default-600">
                    <Icon icon="solar:book-2-linear" className="w-4 h-4" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="font-medium text-foreground truncate">{name}</div>
                    <div className="text-xs text-default-500 mt-0.5">
                      {t('{{count}} entries', { count: entryCount })}
                    </div>
                  </div>
                  <Icon
                    icon="mdi:chevron-right"
                    className={`w-5 h-5 shrink-0 mt-0.5 ${
                      isSelected ? 'text-primary' : 'text-default-400'
                    }`}
                  />
                </div>
              </button>
            );
          })}
      </div>

      <DictionaryPaginationBar
        page={page}
        perPage={perPage}
        totalCount={totalCount}
        onPageChange={onPageChange}
        onPerPageChange={onPerPageChange}
        isLoading={isLoading}
      />
    </aside>
  );
};

export default DictionarySidebar;
